import { computed, type Ref } from 'vue'
import type { BreadcrumbAsset, BreadcrumbItem } from './breadcrumb.types'

export type SearchResult<T> = {
    item: T
    matchedBy: 'name' | 'clientId'
}

type SearchOptions = {
    /**
     * The type to prioritize in search results.
     * - 'portfolio': portfolios appear first
     * - 'asset': assets appear first (with current portfolio's assets at the top)
     */
    priorityType?: Ref<'portfolio' | 'asset' | null>
    /**
     * When priorityType is 'asset', assets from this portfolio appear first
     */
    currentPortfolioId?: Ref<number | null>
}

/**
 * Generic search composable for navigation items
 */
export function useNavigationSearch<T extends BreadcrumbItem>(
    items: Ref<T[]>,
    query: Ref<string>,
    options?: SearchOptions,
) {
    const isSearching = computed(() => query.value.trim().length > 0)

    const searchResults = computed<SearchResult<T>[]>(() => {
        if (!isSearching.value) {
            return []
        }

        const q = query.value.toLowerCase().trim()
        const results: SearchResult<T>[] = []

        for (const item of items.value) {
            const matchesName = (item.name ?? '').toLowerCase().includes(q)
            const matchesAssetClientId = item.type === 'asset'
              && ((item as unknown as BreadcrumbAsset).client_id ?? '').toLowerCase() === q

            if (matchesAssetClientId) {
                results.push({ item, matchedBy: 'clientId' })
            } else if (matchesName) {
                results.push({ item, matchedBy: 'name' })
            }
        }

        // Apply context-aware sorting if options provided
        const priorityType = options?.priorityType?.value
        if (!priorityType) {
            return results
        }

        const currentPortfolioId = options?.currentPortfolioId?.value

        // Lower score = appears first
        const getScore = (result: SearchResult<T>): number => {
            if (priorityType === 'portfolio') {
                // 0: portfolios, 1: assets
                return result.item.type === 'portfolio' ? 0 : 1
            }
            // priorityType === 'asset'
            // 0: assets from current portfolio, 1: other assets, 2: portfolios
            if (result.item.type !== 'asset') {
                return 2
            }
            const isFromCurrentPortfolio = currentPortfolioId && (result.item as unknown as BreadcrumbAsset)?.portfolio?.id === currentPortfolioId
            return isFromCurrentPortfolio ? 0 : 1
        }

        return [...results].sort((a, b) => getScore(a) - getScore(b) || (a.item.name ?? '').localeCompare(b.item.name ?? ''))
    })

    return {
        isSearching,
        searchResults,
    }
}
